import { toEnPath, type Lang } from './routes';

const RELATED_BY_FR_PATH: Record<string, string[]> = {
  '/solutions': ['/solutions/business-model', '/solutions/conformite', '/expertise'],
  '/solutions/business-model': ['/solutions', '/solutions/conformite', '/references'],
  '/solutions/conformite': ['/solutions', '/expertise', '/reprise-parc-informatique'],
  '/expertise': ['/solutions', '/catalogue', '/references'],
  '/references': ['/expertise', '/solutions', '/about'],
  '/about': ['/expertise', '/solutions', '/references'],
  '/catalogue': ['/expertise', '/solutions/business-model', '/buyback'],
  '/contact': ['/solutions', '/catalogue', '/about'],
  '/buyback': ['/reprise-parc-informatique', '/solutions', '/catalogue'],
  '/reprise-parc-informatique': ['/buyback', '/solutions/conformite', '/expertise'],
  '/blog': ['/expertise', '/references', '/solutions'],
};

const PAGE_TITLES: Record<string, Record<Lang, string>> = {
  '/solutions': {
    fr: 'Nos solutions',
    en: 'Our solutions',
  },
  '/solutions/business-model': {
    fr: 'Modèle économique',
    en: 'Business model',
  },
  '/solutions/conformite': {
    fr: 'Conformité réglementaire',
    en: 'Regulatory compliance',
  },
  '/expertise': {
    fr: 'Notre expertise industrielle',
    en: 'Our industrial expertise',
  },
  '/references': {
    fr: 'Ils nous font confiance',
    en: 'They trust us',
  },
  '/about': {
    fr: 'À propos de QBP',
    en: 'About QBP',
  },
  '/catalogue': {
    fr: 'Catalogue produits',
    en: 'Product catalog',
  },
  '/contact': {
    fr: 'Contactez-nous',
    en: 'Contact us',
  },
  '/buyback': {
    fr: 'Buyback B2B',
    en: 'B2B Buyback',
  },
  '/reprise-parc-informatique': {
    fr: 'Reprise de parc informatique',
    en: 'IT fleet buyback',
  },
  '/blog': {
    fr: 'Blog & analyses',
    en: 'Blog & insights',
  },
};

const PAGE_TEASERS: Record<string, Record<Lang, string>> = {
  '/solutions': {
    fr: "Telecom, retail, e-commerce, assurance — découvrez les intégrations sur-mesure.",
    en: 'Telecom, retail, e-commerce, insurance — discover our tailor-made integrations.',
  },
  '/solutions/business-model': {
    fr: 'Zéro stock, marge garantie : vendez avant d’acheter.',
    en: 'Zero stock, guaranteed margin: sell before you buy.',
  },
  '/solutions/conformite': {
    fr: 'AGEC, CSRD, Scope 3 : les données dont vous avez besoin.',
    en: 'AGEC, CSRD, Scope 3: the data you need.',
  },
  '/expertise': {
    fr: '15 ans d’expertise industrielle et 50+ ateliers audités.',
    en: '15 years of industrial expertise and 50+ audited workshops.',
  },
  '/references': {
    fr: 'Témoignages clients dans le retail, la distribution et l’e-commerce.',
    en: 'Customer stories in retail, distribution and e-commerce.',
  },
  '/about': {
    fr: 'L’histoire de QBP Solutions et notre engagement ESS.',
    en: 'The story of QBP Solutions and our social-economy commitment.',
  },
  '/catalogue': {
    fr: '+55 000 références reconditionnées disponibles via API.',
    en: '55,000+ refurbished references available via API.',
  },
  '/contact': {
    fr: 'Parlez à un expert pour structurer votre offre reconditionnée.',
    en: 'Talk to an expert to structure your refurbished offering.',
  },
  '/buyback': {
    fr: 'Programme de reprise en marque blanche, 65+ recycleurs certifiés.',
    en: 'White-label trade-in program, 65+ certified recyclers.',
  },
  '/reprise-parc-informatique': {
    fr: 'Reprise de parc IT : audit physique, effacement certifié, CSRD-ready.',
    en: 'IT fleet buyback: physical audit, certified data wipe, CSRD-ready.',
  },
  '/blog': {
    fr: 'Décryptages sur le reconditionné professionnel et la conformité.',
    en: 'Insights on professional refurbishment and compliance.',
  },
};

export interface RelatedPageItem {
  href: string;
  title: string;
  teaser: string;
}

export function getRelatedPages(
  currentFrPath: string,
  lang: Lang,
): RelatedPageItem[] {
  const relatedFr = RELATED_BY_FR_PATH[currentFrPath];
  if (!relatedFr) return [];

  return relatedFr.flatMap<RelatedPageItem>((frPath) => {
    const title = PAGE_TITLES[frPath]?.[lang];
    const teaser = PAGE_TEASERS[frPath]?.[lang];
    if (!title || !teaser) return [];
    return [
      {
        href: lang === 'fr' ? frPath : toEnPath(frPath),
        title,
        teaser,
      },
    ];
  });
}
